import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
/** Samples spanning the full width: one minute at one sample per second. */
const GRAPH_SAMPLES = 60;
/** SVG viewBox height of the graph (the width is fixed at 100). */
const GRAPH_H = 32;
/** Trailing window of the smoothed average line. */
const AVG_WINDOW = 5;

/**
 * One metric with its current value and a one-minute sparkline: the raw
 * series, its moving average and optionally a reference line (the encoder's
 * bandwidth cap next to the bitrate). Min, max and average use the real
 * samples; the graph is left-padded so a young series spans the full width.
 */
@Component({
  selector: 'andor-stats-graph',
  templateUrl: './stats-graph.html',
  styleUrl: './stats-graph.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsGraph {
  label = input.required<string>();
  series = input.required<readonly number[]>();
  /** Reference line drawn on the same scale, e.g. the bitrate cap. */
  cap = input<readonly number[] | undefined>(undefined);
  /** Overrides the latest sample as the displayed value. */
  value = input<number | undefined>(undefined);
  digits = input(0);
  /**
   * Smallest span the vertical scale covers, widened around the series'
   * middle and kept at or above zero. Without it the scale runs from min to
   * max, so a ping wobbling between 12 and 13 ms fills the full height and
   * reads like a dropout.
   */
  minSpan = input(0);
  tone = input<'ok' | 'warn' | 'error'>('ok');

  protected readonly current = computed(() => {
    const value = this.value() ?? this.series().at(-1);
    return value === undefined ? '–' : fmt(value, this.digits());
  });

  protected readonly summary = computed(() => {
    const series = this.series();
    if (!series.length) return null;
    const digits = this.digits();
    const mean = series.reduce((a, b) => a + b, 0) / series.length;
    return {
      avg: fmt(mean, digits),
      min: fmt(Math.min(...series), digits),
      max: fmt(Math.max(...series), digits),
    };
  });

  protected readonly paths = computed(() => {
    const series = this.series();
    if (!series.length) return null;
    const cap = this.cap();
    const low = Math.min(...series);
    const high = Math.max(...series, ...(cap?.length ? cap : []));
    const span = Math.max(high - low, this.minSpan());
    const min = Math.max(0, (low + high) / 2 - span / 2);
    const max = min + span;
    const raw = padded(series);
    return {
      raw: path(raw, min, max),
      avg: path(movingAvg(raw), min, max),
      cap: cap?.length ? path(padded(cap), min, max) : '',
    };
  });
}

function fmt(value: number, digits: number): string {
  return value.toFixed(digits);
}

function padded(series: readonly number[]): number[] {
  if (series.length >= GRAPH_SAMPLES) return series.slice(-GRAPH_SAMPLES);
  return [...Array<number>(GRAPH_SAMPLES - series.length).fill(series[0]), ...series];
}

/**
 * Smoothed path in the 100×GRAPH_H viewBox: quadratic curves through the
 * segment midpoints, the newest sample pinned to the right edge.
 */
function path(series: number[], min: number, max: number): string {
  const pad = 3;
  const range = max - min;
  const step = 100 / (series.length - 1);
  const pts = series.map((v, i) => ({
    x: i * step,
    y: range === 0 ? GRAPH_H / 2 : GRAPH_H - pad - ((v - min) / range) * (GRAPH_H - 2 * pad),
  }));
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i].x + pts[i + 1].x) / 2;
    const my = (pts[i].y + pts[i + 1].y) / 2;
    d += ` Q ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;
}

function movingAvg(series: number[]): number[] {
  return series.map((_, i) => {
    const slice = series.slice(Math.max(0, i - AVG_WINDOW + 1), i + 1);
    return slice.reduce((a, b) => a + b, 0) / slice.length;
  });
}
